import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import { AdaptiveSingleLineText, Button, SpinnerOverlay } from '@components/common';

import { importMapData } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import { changeFieldName, setDefaultFields } from '@slices/importMapData';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IFieldsMappingDTO } from '@interfaces/import/dto/importDTO';
import { IFieldsSamples, ISystemFields } from '@interfaces/import/import';

import { ESnackbarTypes } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';
import {
	ASSET_ID_SYSTEM_FIELD_NAME,
	componentNames,
	DELAY_FOR_SCROLLING,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/constants';

import CustomValue from './CustomValue';
import FileOptions from './FileOptions';
import SystemFieldName from './SystemFieldName';

import styles from './FieldsMapping.module.scss';

const LAST_FIVE_FIELDS = 5;

interface IFieldsMappingProps {
	setCurrentComponent: React.Dispatch<React.SetStateAction<string>>;
	fieldsSamples: Array<IFieldsSamples>;
	systemFields: Array<ISystemFields>;
	saveFieldsMapping: (value: {
		fieldsMapping: Array<IFieldsMappingDTO>;
		nextClick: () => void;
	}) => void;
	isLoadingFieldsMapping: boolean;
	isDeleteRule: boolean;
	moduleName?: string;
}

const FieldsMapping = ({
	setCurrentComponent,
	fieldsSamples,
	systemFields,
	saveFieldsMapping,
	isLoadingFieldsMapping,
	isDeleteRule,
	moduleName,
}: IFieldsMappingProps) => {
	const { fieldsMapping } = useAppSelector(importMapData);
	const dispatch = useAppDispatch();

	const [isCommonDirty, setIsCommonDirty] = useState(false);

	const [selectOptions, setSelectOptions] = useState(fieldsSamples);

	const selectRefs = useRef<any>([]);
	const countOfFieldsForAutoMenuPlacement = Math.floor(systemFields?.length) - LAST_FIVE_FIELDS;
	const fieldsMappingValues = useMemo(() => Object.values(fieldsMapping), [fieldsMapping]);
	const fieldsMappingDeleteRule = useMemo(() =>
		fieldsMappingValues.find(field => field.systemFieldName === ASSET_ID_SYSTEM_FIELD_NAME), [fieldsMappingValues]);
	const listOfFieldsMapping = useMemo(() => isDeleteRule
		? (fieldsMappingDeleteRule ? [fieldsMappingDeleteRule] : [])
		: fieldsMappingValues, [isDeleteRule, fieldsMappingDeleteRule, fieldsMappingValues]);
	const withDefaultValues = useMemo(() => listOfFieldsMapping
		.some(field => field.hasDefaultValue), [listOfFieldsMapping]);

	useEffect(() => {
		if (!systemFields?.length || !fieldsSamples?.length) return;

		dispatch(setDefaultFields({ systemFields, fieldsSamples, isDeleteRule }));
	}, [fieldsSamples, systemFields, dispatch, isDeleteRule]);

	useEffect(() => {
		if (!fieldsSamples.length) return;

		const listOfSelectedValues = Object.values(fieldsMapping)
			.filter(value => value?.fieldName !== null).map((field) => field?.fieldName);
		const filteredFieldsSamples = fieldsSamples.filter(((option) =>
			!listOfSelectedValues.includes(option?.value || '')));

		setSelectOptions(filteredFieldsSamples);
	}, [fieldsMapping, fieldsSamples]);

	const automapHandler = () => {
		setIsCommonDirty(false);

		dispatch(setDefaultFields({ systemFields, fieldsSamples, isDeleteRule }));
	};

	const clearAllHandler = () => {
		setIsCommonDirty(false);

		dispatch(setDefaultFields({ systemFields, fieldsSamples, isClear: true, isDeleteRule }));
	};

	const handleChangeSelectField = (fieldName: string, option: TSelectOption) => {
		dispatch(changeFieldName({ fieldName, value: option?.value ? option.value as string : null, fieldsSamples }));
	};

	const nextClickCallback = () => setCurrentComponent(componentNames.resultStatistic);

	const handleNextClick = () => {
		setIsCommonDirty(true);

		const isValid = Object.values(fieldsMapping).every((field) => {
			const isRequired = !!field?.isRequired;
			const isUseDefaultValue = !!field?.isUseDefaultValue;
			const defaultValue = field?.defaultValue;
			const fieldName = field?.fieldName;
			const maxValue = field.systemFieldsDefaultData?.maxValue;

			const isPassDefaultValue = (!isRequired && !maxValue) ||
				(
					maxValue
						? (Number(field.defaultValue) <= maxValue)
						: (defaultValue !== null && defaultValue !== undefined)
				);
			const isPassField = !isRequired || !!fieldName?.length;

			return isUseDefaultValue ? isPassDefaultValue : isPassField;
		});

		if (!isValid) {
			dispatch(setSnackbar({
				message: responseMessages.errorFillAll,
				type: ESnackbarTypes.error,
			}));
		} else {
			const fieldsMappingValues: Array<IFieldsMappingDTO> = Object.values(fieldsMapping).map(field => {
				const defaultValue = field.isUseDefaultValue ? field.defaultValue : null;
				const fieldName = !field.isUseDefaultValue ? field.fieldName : null;

				return ({
					systemFieldName: field.systemFieldName,
					defaultValue,
					fieldName,
				});
			});

			saveFieldsMapping({ fieldsMapping: fieldsMappingValues, nextClick: nextClickCallback });
		}
	};

	const moveToCenterHandler = (index: number) => setTimeout(() => {
		selectRefs.current[index].current.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});
	}, DELAY_FOR_SCROLLING);

	return (
		<div className={styles.fieldsMappingContainer}>
			{isLoadingFieldsMapping && <SpinnerOverlay />}
			{!isDeleteRule && <div className={styles.separator} />}
			<div
				className={cn(styles.fieldsWrapper, {
					[styles.deleteRule]: isDeleteRule,
					[styles.withoutDefaultValues]: !withDefaultValues,
				})}
			>
				<div className={cn(styles.title, { [styles.fileFields]: isDeleteRule })}>
					{moduleName ? `${moduleName} ` : ''}
					{isDeleteRule ? 'Attribute' : 'Attributes'}
				</div>
				<div className={cn(styles.title, { [styles.systemFields]: isDeleteRule })}>
					{isDeleteRule ? 'Field in the import file' : 'Fields in the import file'}
				</div>
				{!isDeleteRule
					&& <>
						<div className={cn(styles.title, styles.offsetTitle)}>
							Example
						</div>
						<div className={cn(styles.title, styles.offsetTitle)}>
							Sample value from file
						</div>
						{withDefaultValues
							&& <div className={styles.title}>
								Set common value
							</div>
						}
					</>
				}
				{!!listOfFieldsMapping?.length && listOfFieldsMapping.map((field, index) => {
					const onSelectChange = (option: TSelectOption<string>) => {
						handleChangeSelectField(field.systemFieldName, option);
					};
					const selectValue = fieldsMapping[field.systemFieldName]?.fieldName || null;
					const fileOptionValue = fieldsSamples.find((option) => option?.value === selectValue) || null;
					const isErrorFileOptions = fieldsMapping[field.systemFieldName]?.isUseDefaultValue
						? false
						: !selectValue?.length && !!field.isRequired;
					const menuPlacement = systemFields.length <= LAST_FIVE_FIELDS
						? 'auto'
						: (index < countOfFieldsForAutoMenuPlacement)
							? 'auto'
							: 'top';

					selectRefs.current[index] = selectRefs.current[index] || React.createRef();

					const onSelectClick = () => moveToCenterHandler(index);

					return (
						<React.Fragment key={field.systemFieldName}>
							<SystemFieldName
								isRequired={field.isRequired}
								name={field.title}
								tooltipText={field.tooltip}
							/>
							<div
								ref={selectRefs.current[index]}
								className={styles.select}
								onClick={onSelectClick}
							>
								<FileOptions
									isCommonDirty={isCommonDirty}
									isError={isErrorFileOptions}
									menuPlacement={menuPlacement}
									onSelectChange={onSelectChange}
									selectOptions={selectOptions}
									value={fileOptionValue}
								/>
							</div>
							{!isDeleteRule &&
								<>
									<div className={styles.example}>
										{!!field.example.length
											&& <AdaptiveSingleLineText
												maxWidth={200}
												paddingsSize={0}
												text={field.example}
											/>
										}
									</div>
									<div className={styles.sample}>
										{!!field?.sample?.length
											&& <AdaptiveSingleLineText
												maxWidth={200}
												paddingsSize={0}
												text={field.sample}
											/>
										}
									</div>
									{withDefaultValues
										&& <div className={styles.defaultValue}>
											{field.hasDefaultValue
												&& <CustomValue
													field={field}
													isCommonDirty={isCommonDirty}
													menuPlacement={menuPlacement}
												/>
											}
										</div>
									}
								</>
							}
						</React.Fragment>
					);
				})}
			</div>
			<div className={styles.stickyButtons}>
				<Button
					onClick={clearAllHandler}
					text='Reset all'
					withoutBackground
				/>
				<Button
					onClick={automapHandler}
					secondary
					text='Automap'
				/>
				<Button
					onClick={handleNextClick}
					text='Next'
				/>
			</div>
		</div>
	);
};

export default FieldsMapping;
